import {
  isActionItemType,
  isContentItemType,
  isEmployeeItemType,
  isLocationGroupTypeEnum,
  isLocationItemType,
  isRoleGroupItemType,
  isRoleItemType,
  isRouteItemType,
  isTagItemType,
} from "@src/features/app/top-nav/utils";
import { defaultRouteItemIds } from "@src/features/app/ui/jumpToBar/hooks/routeItems";
import { JumpToItem } from "@src/features/app/ui/jumpToBar/hooks/useAllJumpToItems";
import useSearchJumpToItems from "@src/features/app/ui/jumpToBar/hooks/useSearchJumpToItems";
import ContentAvatar, {
  ContentType,
} from "@src/features/contentLibrary/ContentAvatar";
import {
  EmptyState,
  EmptyStateIcon,
  EmptyStateTitle,
} from "@src/lib/ui/EmptyState";
import SearchIcon from "@src/lib/icons/18px/search";
import GroupAvatar, { GroupType } from "@src/lib/ui/GroupAvatar";
import Avatar from "@src/lib/ui/Avatar";
import { getPillColorsFromId } from "@src/lib/ui/Pill";
import TagOutlineIcon from "@src/lib/icons/18px/tag-outline";
import { cn } from "@src/lib/utils/cn";
import {
  CommandGroup,
  CommandItem,
  CommandSeparator,
} from "@src/lib/ui/Command";
import { FC, useMemo } from "react";

interface Props {
  query: string;
  recentlyViewedItems: JumpToItem[];
  allItems: JumpToItem[];
  loading: boolean;
  onSelectItem: (item: JumpToItem) => void;
  itemDescription: (item: JumpToItem) => string;
  closePopover: () => void;
}

const TagAvatar: FC<{ id: string; size?: "24px" }> = ({
  id,
  size = "24px",
}) => {
  const { bg, icon } = getPillColorsFromId(id);
  return (
    <div
      className={cn(
        "flex items-center justify-center overflow-hidden rounded-md",
        bg,
      )}
      style={{ width: size, height: size }}
    >
      <TagOutlineIcon className={cn("size-4", icon)} />
    </div>
  );
};

const JumpToBarItems: React.FC<Props> = (props) => {
  const showingRecentlyViewed = props.query.trim().length === 0;
  const filteredItems = useSearchJumpToItems(props.allItems, props.query);
  const defaultRouteItems = useMemo(() => {
    const order = new Map(defaultRouteItemIds.map((id, index) => [id, index]));
    return props.allItems
      .filter(
        (item): item is JumpToItem & { type: "route" } =>
          isRouteItemType(item) && order.has(item.id.toString()),
      )
      .sort(
        (a, b) =>
          (order.get(a.id.toString()) ?? 0) - (order.get(b.id.toString()) ?? 0),
      );
  }, [props.allItems]);
  const actionItems = useMemo(
    () => props.allItems.filter(isActionItemType),
    [props.allItems],
  );
  const renderItem = (item: JumpToItem, index: number) => {
    return (
      <CommandItem
        key={index}
        className="gap-2"
        onSelect={() => props.onSelectItem(item)}
        data-testid="jump-to-item-row"
        value={item.id.toString()}
      >
        <div className="flex flex-1 flex-col items-start gap-2">
          <div className="flex w-full items-center gap-2">
            {isEmployeeItemType(item) && typeof item.id === "number" ? (
              <Avatar
                person={{
                  id: item.id,
                  name: item.name,
                  profileImage: item.profileImage,
                }}
                size={24}
                showTooltip
              />
            ) : null}
            {isContentItemType(item) ? (
              <ContentAvatar
                size="24px"
                iconSize={15}
                defaultBackgroundHashKey={item.id}
                contentType={item.type as ContentType}
                coverImage={
                  item.coverImage
                    ? item.coverImage
                    : {
                        imageUrls:
                          item.type === "Resource" && item.mediaUrl
                            ? { original: item.mediaUrl }
                            : undefined,
                      }
                }
              />
            ) : null}
            {isRouteItemType(item) || isActionItemType(item) ? (
              <item.icon className="size-4 text-muted-foreground" />
            ) : null}
            {isLocationItemType(item) ||
            isLocationGroupTypeEnum(item.type) ||
            isRoleItemType(item) ||
            isRoleGroupItemType(item) ? (
              <GroupAvatar size={"24px"} groupType={item.type as GroupType} />
            ) : null}
            {isTagItemType(item) && typeof item.id === "string" ? (
              <TagAvatar id={item.id} />
            ) : null}
            <span className="flex-1">
              {item.name === ""
                ? `Untitled ${item.type}`
                : item.name.replace(/\n/g, " ")}
            </span>
            <span className="text-right text-muted-foreground">
              {props.itemDescription(item).replace(/\n/g, " ")}
            </span>
          </div>
          {item.searchHeadline && (
            <div
              className="px-6 text-sm text-muted-foreground"
              dangerouslySetInnerHTML={{
                __html: item.searchHeadline,
              }}
            />
          )}
        </div>
      </CommandItem>
    );
  };
  if (showingRecentlyViewed) {
    return (
      <>
        <CommandGroup heading="Go to">
          {defaultRouteItems.map((item, index) => renderItem(item, index))}
          {actionItems.map((item, index) =>
            renderItem(item, defaultRouteItems.length + index),
          )}
        </CommandGroup>
        {props.recentlyViewedItems.length > 0 && (
          <>
            <CommandSeparator />
            <CommandGroup heading="Recently Viewed">
              {props.recentlyViewedItems.map((item, index) =>
                renderItem(item, index),
              )}
            </CommandGroup>
          </>
        )}
      </>
    );
  }
  if (filteredItems.length === 0 && props.allItems.length > 0) {
    return (
      <EmptyState loading={props.loading}>
        <EmptyStateIcon>
          <SearchIcon className="size-16" />
        </EmptyStateIcon>
        <EmptyStateTitle>{`No results match "${props.query}"`}</EmptyStateTitle>
      </EmptyState>
    );
  }
  const quickLinks = filteredItems.filter(
    (item) => isRouteItemType(item) || isActionItemType(item),
  );
  const results = filteredItems.filter((item) => !isRouteItemType(item));
  return (
    <>
      {quickLinks.length > 0 && (
        <CommandGroup heading="Go to">
          {quickLinks.map((item, index) => renderItem(item, index))}
        </CommandGroup>
      )}
      {results.length > 0 && (
        <CommandGroup heading="Results">
          {results.map((item, index) => renderItem(item, index))}
        </CommandGroup>
      )}
    </>
  );
};

export default JumpToBarItems;
